import { motion } from 'framer-motion';

export function AboutContent() {
  const stats = [
    { value: '40+', label: 'Years Signarama Experience' },
    { value: '900+', label: 'Locations Worldwide' },
    { value: '5,000+', label: 'Projects Completed' },
    { value: '100%', label: 'Client Satisfaction Goal' },
  ];

  const values = [
    {
      icon: 'ri-palette-line',
      title: 'Creative Excellence',
      description:
        'Every project starts with inspired design. Our team collaborates closely with clients to create signage that is both visually stunning and strategically effective.',
    },
    {
      icon: 'ri-medal-line',
      title: 'Uncompromising Quality',
      description:
        'We use only premium materials — 3M and Avery Dennison vinyl, LED components, and industrial-grade substrates — because your brand deserves nothing less.',
    },
    {
      icon: 'ri-heart-3-line',
      title: 'Local Commitment',
      description:
        'As a locally owned Arlington franchise, we are invested in our community. Your success is our success, and we take personal pride in every sign we produce.',
    },
    {
      icon: 'ri-time-line',
      title: 'Reliability & Speed',
      description:
        'We respect your timeline. Most projects are delivered in 3–7 business days, with same-week rush production available for urgent needs.',
    },
  ];

  const milestones = [
    { year: '1986', event: 'Signarama brand founded — begins transforming the sign industry' },
    { year: '2000s', event: 'Signarama grows to become the world\'s largest sign franchise' },
    { year: '2015', event: 'Signarama Arlington opens to serve the DFW Metroplex' },
    { year: '2020+', event: '5,000+ local projects completed for Arlington area businesses' },
    { year: '2026', event: 'Continuing to lead in custom signage with cutting-edge technology' },
  ];

  return (
    <>
      {/* Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-teal-600 text-sm font-semibold tracking-widest uppercase mb-3 block">
                Our Story
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Built on{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-orange-500">
                  Expertise &amp; Trust
                </span>
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                Signarama Arlington is part of the world&apos;s largest sign franchise — a network
                built on 40+ years of signage expertise, innovation, and a passion for helping
                businesses get noticed. As a locally owned franchise, we bring the power of a
                global brand with the personalized care of a neighborhood partner.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                We serve businesses of every size across Arlington, Grand Prairie, Fort Worth, and
                the entire DFW Metroplex. From a single storefront sign to a complete fleet wrap
                program, our team handles every detail — from concept and design to production and
                professional installation.
              </p>
              <div className="flex items-center space-x-4">
                <a
                  href="tel:817-860-9310"
                  className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-sm font-semibold rounded-full hover:from-teal-600 hover:to-teal-700 transition-all whitespace-nowrap cursor-pointer"
                >
                  Call 817-860-9310
                </a>
                <button
                  onClick={() => {
                    const el = document.getElementById('get-quote');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-6 py-3 border-2 border-teal-500 text-teal-600 text-sm font-semibold rounded-full hover:bg-teal-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  Get a Free Quote
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="grid grid-cols-2 gap-4 mb-6">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl p-6 text-center"
                  >
                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-orange-500 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-gray-600 text-xs">{stat.label}</div>
                  </div>
                ))}
              </div>
              <div className="bg-gray-900 rounded-2xl overflow-hidden w-full h-64">
                <img
                  src="https://cdn.gorilladash.com/images/media/8024538/signarama-usa-dimensional-lettering-48743844537-o-banner-6740b6f63000f.jpg?format=pjpg&auto=webp&quality=90&width=800"
                  alt="Signarama Arlington signage work"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Core{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-orange-500">
                Values
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The principles that guide every project, every decision, and every relationship at
              Signarama Arlington.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-teal-200 transition-all text-center"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline / Milestones */}
      <section className="py-24 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-1/2 w-96 h-96 bg-teal-500 rounded-full blur-3xl -translate-x-1/2"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-orange-400">
                Journey
              </span>
            </h2>
          </motion.div>

          <div className="space-y-6">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start space-x-6"
              >
                <div className="flex-shrink-0 w-20 text-right">
                  <span className="text-teal-400 font-bold text-sm">{milestone.year}</span>
                </div>
                <div className="flex-shrink-0 w-px bg-white/10 self-stretch relative">
                  <div className="absolute top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-teal-500 rounded-full"></div>
                </div>
                <div className="pb-6">
                  <p className="text-gray-300 text-sm leading-relaxed">{milestone.event}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
